import { FC, useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';
import { TbCrosshair, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWatchlists } from '@/queries';
import { EmptySection } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';

interface WatchlistsFilterProps {
    filters: Record<string, unknown>;
    handleChange: (path: string[], value: string[]) => void;
}

const WatchlistsFilter: FC<WatchlistsFilterProps> = ({ filters, handleChange }) => {
    const { token } = useAntDesignTheme();
    const { data: watchlists = [], isLoading } = useWatchlists();
    const [search, setSearch] = useState('');
    const filteredWatchlists = useMemo(
        () =>
            watchlists
                .filter((watchlist) => !watchlist.archived)
                .filter((watchlist) => watchlist.title.toLowerCase().includes(search.trim().toLowerCase())),
        [search, watchlists],
    );

    if (isLoading) {
        return <BaseSkeleton active paragraph={{ rows: 6 }} title={false} />;
    }

    return (
        <>
            <Input
                placeholder='Search Watchlists...'
                prefix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
            />
            {filteredWatchlists.length ? (
                <Checkbox.Group
                    value={Array.isArray(filters.watchlists) ? filters.watchlists : []}
                    style={{ marginTop: '0.75rem', width: '100%' }}
                    onChange={(values) => handleChange(['watchlists'], values.map(String))}
                >
                    {filteredWatchlists.map((watchlist) => (
                        <div
                            key={watchlist.id}
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '0.5rem',
                                marginBottom: '0.375rem',
                                minWidth: 0,
                                width: '100%',
                            }}
                        >
                            <Checkbox id={`search-watchlist-${watchlist.id}`} value={watchlist.id} />
                            <label
                                htmlFor={`search-watchlist-${watchlist.id}`}
                                style={{
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flex: 1,
                                    gap: '0.375rem',
                                    minWidth: 0,
                                }}
                            >
                                <span
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {watchlist.title}
                                </span>
                                <small style={{ color: token.colorTextTertiary, flex: '0 0 auto' }}>
                                    {watchlist.itemCount}
                                </small>
                            </label>
                        </div>
                    ))}
                </Checkbox.Group>
            ) : (
                <EmptySection
                    description={
                        search
                            ? 'No watchlists match your search.'
                            : 'Create a watchlist in Preferences to filter by it.'
                    }
                    height='10rem'
                    icon={<TbCrosshair size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                    label={search ? 'No Watchlists Found' : 'No Watchlists Yet'}
                />
            )}
        </>
    );
};

export default WatchlistsFilter;
